import { eq, or } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import type { NotificationsService } from 'tio-reimagined/components/notifications';
import { restartableTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import ChangeApprover from './my-forms-table/change-approver';
import Component from '@glimmer/component';
import embedPdf from 'tio-common/modifiers/embed-pdf';
import Row from './my-forms-table/row';
import TioConfirmAlert from 'tio-common/components/tio/confirm-alert';
import TioPdfModal from 'tio-common/components/tio/pdf-modal';
import type { IntlService } from 'ember-intl';
import type EmployeeModel from 'tio-common/models/employee';
import type PslfFormModel from 'tio-common/models/pslf-form';
import type RouterService from '@ember/routing/router-service';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type { PaginatedCollection } from 'tio-common/types/ember-data';
import Table from 'tio-reimagined/components/table';
import Paginator from 'tio-reimagined/components/paginator';
import type Owner from '@ember/owner';

export interface PslfMyDocumentsMyFormsTableSignature {
  Args: {
    employee: EmployeeModel;
    limit: number;
  };
  Element: HTMLTableElement;
}

export default class PslfMyDocumentsMyFormsTableComponent extends Component<PslfMyDocumentsMyFormsTableSignature> {
  /////////////////////////////////////
  //////// Services ///////////////////

  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  /////////////////////////////////////
  //////// TRACKED VARIABLES //////////

  @tracked showPreview = false;
  @tracked pdfSrc = '';
  @tracked formToChangeApprover?: PslfFormModel;
  @tracked showDisableConfirm = false;
  @tracked formToDisable?: PslfFormModel;
  @tracked formToResend?: PslfFormModel;
  @tracked page = 1;

  constructor(owner: Owner, args: PslfMyDocumentsMyFormsTableSignature['Args']) {
    super(owner, args);
    this.loadRecords.perform();
  }

  /////////////////////////////////////
  //////// GETTERS  ///////////////////

  get isLoading() {
    return this.loadRecords.isRunning;
  }

  get apiHost() {
    // @ts-expect-error - Ember Data adapter property not typed on MinimumAdapterInterface
    return this.store.adapterFor('document')?.host;
  }

  /////////////////////////////////////
  //////// COMPUTED VARIABLES /////////

  @tracked forms!: PaginatedCollection<PslfFormModel>;

  loadRecords = restartableTask(async () => {
    const sort = '-created-at';
    const page = {
      limit: this.args.limit,
      offset: this.args.limit * (this.page - 1),
    };
    const filter = {
      // Employee could be an ObjectProxy, therefore must use 'get'
      'employee-id': this.args.employee.get('id'),
      'exclude-created': true,
    };
    const include = 'attachments';
    const query = { filter, page, include, sort };
    this.forms = await this.store.query<PslfFormModel>('pslf-form', query);
  });

  doFormAction = (action: string, model: PslfFormModel) => {
    setTimeout(() => {
      if (action === 'changeApprover') {
        this.formToChangeApprover = model;
      }
      if (action === 'cancel') {
        this.formToDisable = model;
        this.showDisableConfirm = true;
      }
      if (action === 'resend') {
        this.resendEmployerEmail(model);
      }
    }, 250);
  };

  openPreviewModal = async (model: PslfFormModel) => {
    setTimeout(() => {
      const url = model.attachments?.[0]?.signedUrl;
      this.pdfSrc = url || '';
      this.showPreview = true;
    }, 250);
  };

  closePreviewModal = () => {
    this.showPreview = false;
    this.pdfSrc = '';
  };

  disableSelectedForm = async () => {
    // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
    await this.store.adapterFor('pslf-form').cancel(this.formToDisable);
    this.showDisableConfirm = false;
  };

  cancelDisable = () => {
    delete this.formToDisable;
    this.showDisableConfirm = false;
  };

  async resendEmployerEmail(model: PslfFormModel) {
    const successMessage = this.intl.t('pslf.document_repo.resend_email_success', {
      email: model.approverDisplayEmail,
    });
    const errorMessage = this.intl.t('pslf.document_repo.resend_email_error', {
      email: model.approverDisplayEmail,
    });
    this.formToResend = model;
    try {
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      await this.store.adapterFor('pslf-form').resendEmployerEmail(model);
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    } catch (err) {
      this.notifications.add(errorMessage, {
        appearance: 'error',
      });
    } finally {
      delete this.formToResend;
    }
  }

  didChangeApprover = (model: PslfFormModel) => {
    delete this.formToChangeApprover;
    if (model.canResendEmail) {
      this.resendEmployerEmail(model);
    }
  };

  previousPage = () => {
    this.page = Math.max(1, this.page - 1);
    this.loadRecords.perform();
  };

  nextPage = () => {
    ++this.page;
    this.loadRecords.perform();
  };

  changePaginatorPage = (page: number) => {
    this.page = page;
    this.loadRecords.perform();
  };

  get totalPages() {
    const meta = this.forms?.meta;

    if (meta?.page?.total) {
      return Math.ceil(meta.page.total / this.args.limit);
    }
    return 0;
  }

  <template>
    <Table @isLoading={{this.isLoading}} ...attributes>
      <:thead as |options|>
        <options.tr>
          <options.th>{{t "pslf.document_repo.date_employer_signed"}}</options.th>
          <options.th>{{t "pslf.document_repo.date_you_signed"}}</options.th>
          <options.th>{{t "common.status"}}</options.th>
          <options.th>{{t "pslf.document_repo.approver_info"}}</options.th>
          <options.th>{{t "pslf.document_repo.form_type_and_employer"}}</options.th>
          <options.th class="whitespace-nowrap" />
        </options.tr>
      </:thead>
      <:tbody as |options|>
        {{#each this.forms as |form|}}
          <Row
            @doFormAction={{this.doFormAction}}
            @model={{form}}
            @onPreview={{this.openPreviewModal}}
            @isResending={{eq this.formToResend.id form.id}}
            @isLoading={{false}}
            @cellClass={{options.cellClass}}
          />
        {{else}}
          <options.tr>
            <options.td colspan="7">
              {{t "pslf.document_repo.no_forms_found"}}
            </options.td>
          </options.tr>
        {{/each}}
      </:tbody>
      <:tfoot>
        <tr>
          <td class="text-right" colspan="9">
            <Paginator
              @simple={{true}}
              @currentPage={{this.page}}
              @totalItems={{or this.forms.meta.page.total 0}}
              @pageSize={{@limit}}
              @onChange={{this.changePaginatorPage}}
            />
          </td>
        </tr>
      </:tfoot>
    </Table>
    <TioPdfModal @show={{this.showPreview}} @onClose={{this.closePreviewModal}}>
      {{#if this.pdfSrc}}
        <div class="w-full h-full max-w-4xl mx-auto">
          <div {{embedPdf this.pdfSrc}}></div>
        </div>
      {{/if}}
    </TioPdfModal>

    <TioConfirmAlert
      @show={{this.showDisableConfirm}}
      @onConfirm={{this.disableSelectedForm}}
      @onCancel={{this.cancelDisable}}
      @confirmButtonText={{t "disable"}}
      @isDestructive={{true}}
      @disabled={{false}}
    >
      <p class="mb-4 font-semibold text-lg">
        {{t "pslf.document_repo.confirm_disable_title"}}
      </p>
      <p>{{t "pslf.document_repo.confirm_disable_prompt_1"}}</p>
      <p class="font-semibold">
        {{t "pslf.document_repo.confirm_disable_prompt_2"}}
      </p>
    </TioConfirmAlert>

    {{#if this.formToChangeApprover}}
      <ChangeApprover
        {{! @glint-expect-error: need to research mut helper}}
        @didCancel={{fn (mut this.formToChangeApprover) null}}
        {{! @glint-expect-error: not sure why we don't pass model here}}
        @didSave={{this.didChangeApprover}}
        @form={{this.formToChangeApprover}}
      />
    {{/if}}
  </template>
}
